import { useEffect, useMemo, useRef, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import {
  Search,
  FileText,
  ShieldCheck,
  CalendarClock,
  AlertTriangle,
  Repeat,
  Mail,
  X,
  Plus,
  Archive,
  Download,
  BookOpen,
  Trash2,
  Loader2,
  FileType2,
  Upload,
  Sparkles,
  ShieldAlert,
  RefreshCw,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import FeatureTour from "../components/FeatureTour";

const VAULT_TOUR_STEPS = [
  {
    target: '[data-testid^="binder-email-cpa-"]',
    placement: "left",
    title: "📧 Email any binder to your CPA",
    content:
      "One click ships the year-end binder PDF to every CPA contact you've saved — including your email signature. Perfect during tax season; works whether you're using BYO SMTP or our default delivery.",
    disableBeacon: true,
  },
];

/**
 * Lease Vault — searchable, year-filterable inventory of every lease in the
 * investor's portfolio with downloadable artifacts (signed e-sig PDF, custom
 * uploaded PDF, draft generator) and one-click year-end CPA email export.
 *
 * Backed by:
 *   GET /api/lease-vault?year=&q=
 *   GET /api/lease-vault/cpa-contacts
 *   PUT /api/lease-vault/cpa-contacts
 *   POST /api/lease-vault/email-cpa
 *   GET /api/leases/{lid}/agreement/pdf  (draft / clause-overridden / custom-uploaded)
 *   GET /api/esignature/envelopes/{eid}/signed-pdf  (executed PDF)
 */
export default function LeaseVault() {
  const [year, setYear] = useState("all");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (year !== "all") params.year = year;
      if (query.trim()) params.q = query.trim();
      const r = await api.get("/lease-vault", { params });
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load vault");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      load();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const items = data?.items || [];
  const summary = data?.summary || {};
  const filteredItems = useMemo(() => {
    if (statusFilter === "all") return items;
    if (statusFilter === "signed") return items.filter((i) => i.signed_pdf_available);
    if (statusFilter === "custom") return items.filter((i) => i.has_custom_pdf);
    return items.filter((i) => i.status === statusFilter);
  }, [items, statusFilter]);

  const downloadDraft = async (item) => {
    try {
      toast.loading("Generating PDF…", { id: `vault-pdf-${item.lease_id}` });
      const safeProp = (item.property_name || "property").replace(/[^A-Za-z0-9_-]+/g, "_");
      const safeTenant = (item.tenant_name || "tenant").replace(/[^A-Za-z0-9_-]+/g, "_");
      await authenticatedDownload(
        `/api/leases/${item.lease_id}/agreement/pdf`,
        `lease-${safeProp}-${safeTenant}.pdf`,
      );
      toast.success("Lease downloaded", { id: `vault-pdf-${item.lease_id}` });
    } catch {
      toast.error("Download failed", { id: `vault-pdf-${item.lease_id}` });
    }
  };

  const downloadSigned = async (item) => {
    if (!item.envelope_id) return;
    try {
      toast.loading("Fetching signed PDF…", { id: `vault-signed-${item.lease_id}` });
      const safeProp = (item.property_name || "property").replace(/[^A-Za-z0-9_-]+/g, "_");
      await authenticatedDownload(
        `/api/esignature/envelopes/${item.envelope_id}/signed-pdf`,
        `lease-signed-${safeProp}.pdf`,
      );
      toast.success("Signed PDF downloaded", { id: `vault-signed-${item.lease_id}` });
    } catch {
      toast.error("Could not download signed PDF", { id: `vault-signed-${item.lease_id}` });
    }
  };

  const downloadDocx = async (item) => {
    try {
      toast.loading("Generating Word document…", { id: `vault-docx-${item.lease_id}` });
      const safeProp = (item.property_name || "property").replace(/[^A-Za-z0-9_-]+/g, "_");
      const safeTenant = (item.tenant_name || "tenant").replace(/[^A-Za-z0-9_-]+/g, "_");
      await authenticatedDownload(
        `/api/leases/${item.lease_id}/agreement/docx`,
        `lease-${safeProp}-${safeTenant}.docx`,
      );
      toast.success("Word document downloaded", { id: `vault-docx-${item.lease_id}` });
    } catch {
      toast.error("Word download failed", { id: `vault-docx-${item.lease_id}` });
    }
  };

  const uploadDocx = async (item, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      toast.error("Please choose a .docx file");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.loading(`Uploading ${file.name}…`, { id: `vault-up-${item.lease_id}` });
      await api.post(`/leases/${item.lease_id}/agreement/upload-docx`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Word file uploaded — it will now be the served version", { id: `vault-up-${item.lease_id}` });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed", { id: `vault-up-${item.lease_id}` });
    }
  };

  const generateResidentialAi = async (item) => {
    try {
      toast.loading("Queueing Claude — drafting state-specific lease in the background…", {
        id: `vault-ai-${item.lease_id}`,
        duration: 4000,
      });
      await api.post(`/leases/${item.lease_id}/agreement/generate-residential-ai`);
      toast.success(
        "Drafting started. This usually takes 60–90 seconds — refresh in a moment to see the AI lease.",
        { id: `vault-ai-${item.lease_id}`, duration: 6000 },
      );
      load();
      setTimeout(() => load(), 90_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI draft failed", {
        id: `vault-ai-${item.lease_id}`,
      });
    }
  };

  const triggerRegulatoryScan = async (item) => {
    const state = item.ai_state || "CA";
    try {
      toast.loading(`Scanning ${state} for recent residential statute amendments…`, {
        id: `vault-reg-${item.lease_id}`,
        duration: 4000,
      });
      await api.post(`/regulatory-scans/${state}`);
      toast.success(
        `Scan queued. Claude will analyze recent ${state} amendments and flag any leases needing review (~60s).`,
        { id: `vault-reg-${item.lease_id}`, duration: 6000 },
      );
      load();
      setTimeout(() => load(), 90_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Regulatory scan failed", {
        id: `vault-reg-${item.lease_id}`,
      });
    }
  };

  const redraftAffectedClauses = async (item) => {
    const count = (item.regulatory_alerts || []).length;
    if (!count) {
      toast.error("No flagged amendments — nothing to redraft");
      return;
    }
    if (!window.confirm(
      `Re-draft ${count} affected clause${count === 1 ? "" : "s"} for ${item.property_name}?\n\n` +
      `Only the clauses tied to flagged amendments will be regenerated. ` +
      `Every other clause stays exactly as it is now (including any manual edits).`
    )) return;
    try {
      toast.loading("Surgical re-draft queued — Claude will rewrite only the affected clauses…", {
        id: `vault-redraft-${item.lease_id}`,
        duration: 4000,
      });
      await api.post(`/leases/${item.lease_id}/agreement/redraft-affected-clauses`);
      toast.success(
        "Re-draft started. Affected clauses will refresh in ~60s; alerts will clear when done.",
        { id: `vault-redraft-${item.lease_id}`, duration: 6000 },
      );
      load();
      setTimeout(() => load(), 90_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Re-draft failed", {
        id: `vault-redraft-${item.lease_id}`,
      });
    }
  };

  return (
    <DashboardShell>
      <FeatureTour
        featureId="lease-vault-binder-email"
        steps={VAULT_TOUR_STEPS}
        waitForSelector='[data-testid^="binder-email-cpa-"]'
        delayMs={1200}
      />
      <div className="space-y-6" data-testid="lease-vault-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500" style={{ fontFamily: "Outfit" }}>
              Document Room
            </div>
            <h1 className="text-3xl text-slate-900" style={{ fontFamily: "Outfit", fontWeight: 700 }}>
              Lease Vault
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Every executed, drafted, or attorney-supplied lease agreement across your portfolio — searchable, year-filterable, audit-ready.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEmailOpen(true)}
              className="btn-primary-dark inline-flex items-center gap-2"
              data-testid="vault-email-cpa-button"
            >
              <Mail className="w-4 h-4" /> Send to CPA
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="vault-summary">
          <SummaryTile label="Active" value={summary.active || 0} icon={ShieldCheck} accent="text-emerald-700" bgClass="bg-green-50 border-green-200" />
          <SummaryTile label="Expiring (≤90d)" value={summary.expiring || 0} icon={CalendarClock} accent="text-amber-700" bgClass="bg-yellow-50 border-yellow-200" />
          <SummaryTile label="Expired" value={summary.expired || 0} icon={AlertTriangle} accent="text-rose-700" bgClass="bg-red-50 border-red-200" />
          <SummaryTile label="Renewed" value={summary.renewed || 0} icon={Repeat} accent="text-sky-700" bgClass="bg-amber-50 border-amber-200" />
          <SummaryTile label="Signed PDFs on file" value={summary.with_signed_pdf || 0} icon={FileText} accent="text-violet-700" bgClass="bg-purple-50 border-purple-200" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border border-slate-200 bg-white rounded-sm p-3" data-testid="vault-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400 pointer-events-none" />
            <input
              type="search"
              placeholder="Search property, tenant, unit…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input-clean w-full pl-9"
              data-testid="vault-search"
            />
          </div>
          <select
            className="input-clean"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            data-testid="vault-year-select"
          >
            <option value="all">All years</option>
            {(data?.available_years || []).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <div className="flex items-center gap-1 flex-wrap">
            <FilterPill id="all" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} count={items.length}>All</FilterPill>
            <FilterPill id="active" active={statusFilter === "active"} onClick={() => setStatusFilter("active")} count={summary.active || 0} accent="emerald">Active</FilterPill>
            <FilterPill id="expiring" active={statusFilter === "expiring"} onClick={() => setStatusFilter("expiring")} count={summary.expiring || 0} accent="amber">Expiring</FilterPill>
            <FilterPill id="expired" active={statusFilter === "expired"} onClick={() => setStatusFilter("expired")} count={summary.expired || 0} accent="rose">Expired</FilterPill>
            <FilterPill id="renewed" active={statusFilter === "renewed"} onClick={() => setStatusFilter("renewed")} count={summary.renewed || 0} accent="sky">Renewed</FilterPill>
            <FilterPill id="signed" active={statusFilter === "signed"} onClick={() => setStatusFilter("signed")} count={summary.with_signed_pdf || 0} accent="violet">Signed</FilterPill>
            <FilterPill id="custom" active={statusFilter === "custom"} onClick={() => setStatusFilter("custom")} count={summary.with_custom_pdf || 0} accent="violet">Custom PDF</FilterPill>
          </div>
        </div>

        {/* Results table */}
        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden" data-testid="vault-list">
          {loading ? (
            <div className="py-16 text-center text-sm text-slate-500">Loading vault…</div>
          ) : filteredItems.length === 0 ? (
            <EmptyState query={query} year={year} statusFilter={statusFilter} />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left px-4 py-3">Property · Unit</th>
                  <th className="text-left px-4 py-3">Tenant</th>
                  <th className="text-left px-4 py-3">Term</th>
                  <th className="text-right px-4 py-3">Rent</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Documents</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <VaultRow
                    key={item.lease_id}
                    item={item}
                    onDownloadDraft={() => downloadDraft(item)}
                    onDownloadSigned={() => downloadSigned(item)}
                    onDownloadDocx={() => downloadDocx(item)}
                    onUploadDocx={(file) => uploadDocx(item, file)}
                    onGenerateResidentialAi={() => generateResidentialAi(item)}
                    onScanRegulatory={() => triggerRegulatoryScan(item)}
                    onRedraftAffected={() => redraftAffectedClauses(item)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Year-end binders panel */}
        <BindersPanel availableYears={data?.available_years || []} />
      </div>

      <EmailCpaDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        defaultYear={year !== "all" ? year : (data?.available_years?.[0] ?? null)}
        availableYears={data?.available_years || []}
      />
    </DashboardShell>
  );
}

function SummaryTile({ label, value, icon: Icon, accent, bgClass = "bg-white border-slate-200" }) {
  return (
    <div className={`${bgClass} border rounded-sm px-4 py-3`}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500" style={{ fontFamily: "Outfit" }}>
        <Icon className={`w-3.5 h-3.5 ${accent || "text-slate-500"}`} /> {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1 font-mono">{value}</div>
    </div>
  );
}

function FilterPill({ id, active, onClick, count, children, accent = "slate" }) {
  const base = "text-xs font-semibold px-2.5 py-1.5 rounded-sm border transition-colors inline-flex items-center gap-1.5";
  const cls = active
    ? "bg-slate-900 text-white border-slate-900"
    : "bg-white text-slate-600 hover:text-slate-900 border-slate-200 hover:border-slate-400";
  const _accent = accent; // reserved for future accent overrides
  void _accent;
  return (
    <button onClick={onClick} className={`${base} ${cls}`} data-testid={`vault-filter-${id}`}>
      {children}
      <span className={`text-[10px] px-1 rounded-sm ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );
}

function VaultRow({ item, onDownloadDraft, onDownloadSigned, onDownloadDocx, onUploadDocx, onGenerateResidentialAi, onScanRegulatory, onRedraftAffected }) {
  const fileInputRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [redrafting, setRedrafting] = useState(false);
  const handleGenerateAi = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await onGenerateResidentialAi();
    } finally {
      setGenerating(false);
    }
  };
  const handleScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      await onScanRegulatory();
    } finally {
      setScanning(false);
    }
  };
  const handleRedraft = async () => {
    if (redrafting) return;
    setRedrafting(true);
    try {
      await onRedraftAffected();
    } finally {
      setRedrafting(false);
    }
  };
  const alerts = item.regulatory_alerts || [];
  const highSeverity = alerts.some((a) => (a.severity || "").toLowerCase() === "high");
  const statusColors = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    expiring: "bg-amber-50 text-amber-900 border-amber-200",
    expired: "bg-rose-50 text-rose-900 border-rose-200",
    renewed: "bg-sky-50 text-sky-900 border-sky-200",
  };
  const cls = statusColors[item.status] || "bg-slate-50 text-slate-700 border-slate-200";
  const leaseTypeMap = { general: "General", gross: "Gross", triple_net: "Triple Net" };
  const daysLabel = item.days_left == null
    ? "—"
    : item.days_left < 0
      ? `${Math.abs(item.days_left)}d ago`
      : `${item.days_left}d left`;

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`vault-row-${item.lease_id}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-slate-900">{item.property_name}</div>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
          <span>{item.unit_label || "—"}</span>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">
            {leaseTypeMap[item.lease_type] || "General"}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-slate-700">{item.tenant_name}</div>
        <div className="text-xs text-slate-400">{item.tenant_email}</div>
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 font-mono whitespace-nowrap">
        {item.start_date || "—"} → {item.end_date || "—"}
        <div className="text-[11px] text-slate-400 mt-0.5">{daysLabel}</div>
      </td>
      <td className="px-4 py-3 text-right font-mono font-bold text-slate-900">
        ${(item.monthly_rent || 0).toLocaleString()}
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`} data-testid={`vault-status-${item.lease_id}`}>
          {item.status}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1 flex-wrap">
          {item.signed_pdf_available ? (
            <button
              onClick={onDownloadSigned}
              title="Download fully executed (signed) PDF"
              className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
              data-testid={`vault-download-signed-${item.lease_id}`}
            >
              <ShieldCheck className="w-3 h-3" /> Signed
            </button>
          ) : null}
          {item.has_custom_pdf ? (
            <span
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-violet-50 text-violet-800 border border-violet-200 inline-flex items-center gap-1"
              title={item.custom_pdf_filename || "Attorney-drafted custom PDF"}
              data-testid={`vault-custom-pdf-${item.lease_id}`}
            >
              Custom PDF
            </span>
          ) : null}
          {item.has_clause_overrides && !item.has_custom_pdf ? (
            <span
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200"
              title="One or more clauses are overridden"
              data-testid={`vault-overrides-${item.lease_id}`}
            >
              Overrides
            </span>
          ) : null}
          {item.lease_category === "residential" && (() => {
            const drafting = item.ai_status === "drafting";
            const errored = item.ai_status === "error";
            let cls = "bg-rose-50 text-rose-700 border-rose-200";
            let label = "Residential · Pending";
            let title = "Residential lease — AI generation pending";
            if (item.has_ai_clauses) {
              cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
              label = `Residential · ${item.ai_state || "AI"}`;
              title = `AI-drafted for ${item.ai_state || "state"}`;
            } else if (drafting) {
              cls = "bg-violet-50 text-violet-800 border-violet-200 animate-pulse";
              label = "Residential · Drafting…";
              title = "Claude is drafting this lease — refresh in 60–90s";
            } else if (errored) {
              cls = "bg-rose-50 text-rose-800 border-rose-300";
              label = "Residential · AI failed";
              title = item.ai_error || "AI generation failed — click 'AI draft' to retry";
            }
            return (
              <span
                className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}
                title={title}
                data-testid={`vault-residential-${item.lease_id}`}
              >
                {label}
              </span>
            );
          })()}
          {item.lease_category === "residential" && item.has_ai_clauses && (
            <button
              onClick={handleScan}
              disabled={scanning}
              title={alerts.length > 0
                ? `${alerts.length} amendment${alerts.length === 1 ? "" : "s"} flagged${highSeverity ? " (high severity)" : ""}. Click to re-scan.`
                : item.regulatory_last_scanned_at
                  ? `Last scan ${new Date(item.regulatory_last_scanned_at).toLocaleDateString()} — no amendments flagged. Click to re-scan.`
                  : "Click to scan recent statute amendments for this state"}
              className={`text-[10px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border ${
                alerts.length > 0
                  ? (highSeverity
                    ? "bg-rose-50 text-rose-800 border-rose-300 animate-pulse"
                    : "bg-amber-50 text-amber-800 border-amber-300")
                  : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
              } disabled:opacity-60`}
              data-testid={`vault-regulatory-${item.lease_id}`}
            >
              {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> :
                alerts.length > 0 ? <ShieldAlert className="w-3 h-3" /> : <RefreshCw className="w-3 h-3" />}
              {scanning ? "Scanning…" :
                alerts.length > 0 ? `${alerts.length} update${alerts.length === 1 ? "" : "s"}` : "Reg-scan"}
            </button>
          )}
          {item.lease_category === "residential" && alerts.length > 0 && (
            <button
              onClick={handleRedraft}
              disabled={redrafting || item.ai_status === "redrafting"}
              title={`Re-draft only the ${alerts.length} affected clause${alerts.length === 1 ? "" : "s"} — manual edits in unaffected sections survive`}
              className="text-[10px] font-semibold inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border bg-amber-100 text-amber-900 border-amber-400 hover:bg-amber-200 disabled:opacity-60 disabled:cursor-wait"
              data-testid={`vault-redraft-affected-${item.lease_id}`}
            >
              {redrafting || item.ai_status === "redrafting"
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Wand2 className="w-3 h-3" />}
              {redrafting || item.ai_status === "redrafting" ? "Re-drafting…" : "Fix clauses"}
            </button>
          )}
          <button
            onClick={onDownloadDraft}
            title={item.has_custom_pdf ? "Download the custom uploaded PDF" : "Download the auto-generated lease draft"}
            className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-200 text-slate-700 hover:bg-slate-100"
            data-testid={`vault-download-pdf-${item.lease_id}`}
          >
            <Download className="w-3 h-3" /> {item.has_custom_pdf ? "Custom" : "Draft"}
          </button>
          <button
            onClick={onDownloadDocx}
            title="Download as editable Word (.docx) — open in MS Word, mark up, then re-upload"
            className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
            data-testid={`vault-download-docx-${item.lease_id}`}
          >
            <FileType2 className="w-3 h-3" /> Word
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            data-testid={`vault-upload-docx-input-${item.lease_id}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadDocx(file);
              e.target.value = ""; // allow re-uploading same filename
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Upload your edited Word file — it replaces the served version of this lease"
            className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-blue-200 text-blue-700 hover:bg-blue-100"
            data-testid={`vault-upload-docx-btn-${item.lease_id}`}
          >
            <Upload className="w-3 h-3" /> Re-upload
          </button>
          {item.lease_category === "residential" && (
            <button
              onClick={handleGenerateAi}
              disabled={generating}
              title={item.has_ai_clauses ? "Re-run Claude — overwrites the existing draft" : "Draft state-specific residential lease using Claude Sonnet 4.5"}
              className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-violet-300 text-violet-800 bg-violet-50 hover:bg-violet-100 disabled:opacity-60 disabled:cursor-wait"
              data-testid={`vault-generate-ai-${item.lease_id}`}
            >
              {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {generating ? "Drafting…" : (item.has_ai_clauses ? "Re-draft AI" : "AI draft")}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function EmptyState({ query, year, statusFilter }) {
  const hasFilter = query || year !== "all" || statusFilter !== "all";
  return (
    <div className="py-16 text-center px-6" data-testid="vault-empty">
      <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
        <Archive className="w-6 h-6" />
      </div>
      <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
        {hasFilter ? "No leases match this view" : "Your vault is empty"}
      </div>
      <p className="text-sm text-slate-500 mt-1">
        {hasFilter
          ? "Try clearing the search or selecting a different year / status."
          : "Add leases on the Leases page — every draft, custom upload, and signed PDF will appear here."}
      </p>
    </div>
  );
}

function BindersPanel({ availableYears }) {
  const [binders, setBinders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [year, setYear] = useState("");
  const [emailCpa, setEmailCpa] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [prefLoading, setPrefLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/lease-vault/binders");
      setBinders(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load binders");
    } finally {
      setLoading(false);
    }
  };

  const loadPref = async () => {
    try {
      const r = await api.get("/lease-vault/binders/preference");
      setAutoEnabled(r.data?.annual_binder_enabled !== false);
    } catch {
      // non-fatal
    }
  };

  useEffect(() => {
    load();
    loadPref();
  }, []);

  // Default to the most recent completed calendar year
  useEffect(() => {
    if (!year && availableYears.length > 0) {
      const now = new Date().getUTCFullYear();
      const lastYear = availableYears.find((y) => y < now) ?? availableYears[0];
      setYear(String(lastYear));
    }
  }, [availableYears, year]);

  const generate = async () => {
    if (!year) {
      toast.error("Pick a year first");
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post("/lease-vault/binders", {
        year: Number(year),
        email_to_cpa: emailCpa,
      });
      const snap = r.data?.snapshot;
      const lc = snap?.lease_count || 0;
      toast.success(`Binder generated · ${lc} lease${lc === 1 ? "" : "s"}${emailCpa ? " · CPA emailed" : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const downloadBinder = async (b) => {
    try {
      toast.loading("Fetching binder…", { id: `binder-${b.id}` });
      await authenticatedDownload(
        `/api/lease-vault/binders/${b.id}/download`,
        `lease-binder-${b.year}.pdf`,
      );
      toast.success("Binder downloaded", { id: `binder-${b.id}` });
    } catch {
      toast.error("Download failed", { id: `binder-${b.id}` });
    }
  };

  const deleteBinder = async (b) => {
    if (!confirm(`Delete the ${b.year} binder snapshot? The PDF file in object storage is retained for legal-retention purposes; only this metadata row is removed.`)) return;
    try {
      await api.delete(`/lease-vault/binders/${b.id}`);
      toast.success("Snapshot deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const emailBinderToCpa = async (b) => {
    try {
      toast.loading(`Emailing ${b.year} binder to your CPA…`, { id: `binder-email-${b.id}` });
      const r = await api.post(`/lease-vault/binders/${b.id}/email-cpa`, {});
      const recipients = r.data?.recipients || [];
      const status = r.data?.result?.status || "sent";
      if (status === "mock") {
        toast.message(`MOCK · would have emailed ${b.year} binder`, {
          id: `binder-email-${b.id}`,
          description: `To: ${recipients.join(", ") || "—"} · configure RESEND_API_KEY or BYO SMTP to send live`,
        });
      } else {
        toast.success(`Emailed ${b.year} binder to ${recipients.length} CPA${recipients.length === 1 ? "" : "s"}`, {
          id: `binder-email-${b.id}`,
          description: recipients.join(", "),
        });
      }
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || "Email failed";
      // 400 typically means no CPA contacts saved — guide the investor to fix it.
      if (err.response?.status === 400 && /No recipients/i.test(detail)) {
        toast.error("Save a CPA email first", {
          id: `binder-email-${b.id}`,
          description: "Click 'Email CPA' at the top of the vault and add at least one address.",
        });
      } else {
        toast.error(detail, { id: `binder-email-${b.id}` });
      }
    }
  };

  const togglePref = async () => {
    setPrefLoading(true);
    try {
      const next = !autoEnabled;
      await api.put("/lease-vault/binders/preference", { annual_binder_enabled: next });
      setAutoEnabled(next);
      toast.success(next ? "Auto-binder enabled (Jan 1)" : "Auto-binder disabled");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update preference");
    } finally {
      setPrefLoading(false);
    }
  };

  return (
    <div className="border border-slate-200 bg-white rounded-sm" data-testid="vault-binders-panel">
      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
        <BookOpen className="w-4 h-4 text-slate-700" />
        <div className="flex-1">
          <div className="font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Year-end binders
          </div>
          <div className="text-xs text-slate-500">
            Frozen, immutable PDF snapshots of every lease in a calendar year. Auto-generated on Jan 1; available on demand.
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" title="Toggle the Jan 1 auto-binder cron">
          <input
            type="checkbox"
            checked={autoEnabled}
            onChange={togglePref}
            disabled={prefLoading}
            data-testid="binder-auto-toggle"
          />
          Auto-generate every Jan&nbsp;1
        </label>
      </div>

      <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1" style={{ fontFamily: "Outfit" }}>Year</label>
          <select
            className="input-clean"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            data-testid="binder-year-select"
          >
            {availableYears.length === 0 ? (
              <option value="">No leases yet</option>
            ) : (
              availableYears.map((y) => <option key={y} value={y}>{y}</option>)
            )}
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer pb-2">
          <input
            type="checkbox"
            checked={emailCpa}
            onChange={(e) => setEmailCpa(e.target.checked)}
            data-testid="binder-email-toggle"
          />
          Also email saved CPA contacts
        </label>
        <button
          onClick={generate}
          disabled={generating || !year || availableYears.length === 0}
          className="btn-primary-dark inline-flex items-center gap-2"
          data-testid="binder-generate-button"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
          {generating ? "Generating…" : "Generate binder"}
        </button>
      </div>

      <div className="divide-y divide-slate-100" data-testid="binder-list">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">Loading binders…</div>
        ) : binders.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500" data-testid="binder-empty">
            No binders yet. Generate your first to lock in this year's lease state.
          </div>
        ) : (
          binders.map((b) => (
            <div key={b.id} className="px-5 py-3 flex items-center gap-3" data-testid={`binder-row-${b.id}`}>
              <div className="w-9 h-9 bg-slate-900 text-white rounded-sm flex items-center justify-center font-bold text-xs">
                {b.year}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">
                  {b.year} year-end binder
                  {b.source === "scheduled" ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200">
                      Auto
                    </span>
                  ) : (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">
                      On demand
                    </span>
                  )}
                  {!b.storage_ok ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-800 border border-rose-200">
                      Storage error
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {b.lease_count} lease{b.lease_count === 1 ? "" : "s"} · {(b.size_bytes / 1024).toFixed(0)} KB · {new Date(b.created_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => downloadBinder(b)}
                disabled={!b.storage_ok}
                className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                data-testid={`binder-download-${b.id}`}
              >
                <Download className="w-3 h-3" /> PDF
              </button>
              <button
                onClick={() => emailBinderToCpa(b)}
                disabled={!b.storage_ok}
                title="Email this binder PDF to your saved CPA contacts"
                className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-40"
                data-testid={`binder-email-cpa-${b.id}`}
              >
                <Mail className="w-3 h-3" /> Email CPA
              </button>
              <button
                onClick={() => deleteBinder(b)}
                className="text-slate-400 hover:text-rose-600 p-1"
                title="Delete this snapshot"
                data-testid={`binder-delete-${b.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function EmailCpaDialog({ open, onClose, defaultYear, availableYears }) {
  const [recipients, setRecipients] = useState([]);
  const [draftEmail, setDraftEmail] = useState("");
  const [year, setYear] = useState("all");
  const [note, setNote] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  // SMS-the-CPA toggle (sends a heads-up SMS when the CPA email goes out).
  const [cpaPhone, setCpaPhone] = useState("");
  const [smsOnBinder, setSmsOnBinder] = useState(false);
  const [savingSms, setSavingSms] = useState(false);

  useEffect(() => {
    if (!open) return;
    setYear(defaultYear ? String(defaultYear) : "all");
    setLoadingContacts(true);
    api.get("/lease-vault/cpa-contacts")
      .then((r) => {
        setRecipients(r.data?.cpa_emails || []);
        setCpaPhone(r.data?.cpa_phone || "");
        setSmsOnBinder(!!r.data?.cpa_sms_on_binder_email);
      })
      .catch(() => { setRecipients([]); setCpaPhone(""); setSmsOnBinder(false); })
      .finally(() => setLoadingContacts(false));
    setNote("");
    setDraftEmail("");
    setSaveDefault(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Persists the SMS prefs (phone + toggle) without sending the email itself.
  // Surfaced via a "Save SMS settings" button so users can configure once
  // even if they're not sending the binder right this minute.
  const saveSmsPrefs = async () => {
    setSavingSms(true);
    try {
      const { data } = await api.put("/lease-vault/cpa-contacts", {
        cpa_emails: recipients,
        cpa_phone: cpaPhone.trim() || null,
        cpa_sms_on_binder_email: smsOnBinder,
      });
      setCpaPhone(data?.cpa_phone || "");
      setSmsOnBinder(!!data?.cpa_sms_on_binder_email);
      toast.success("SMS settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save SMS settings");
    } finally {
      setSavingSms(false);
    }
  };

  const addRecipient = () => {
    const v = draftEmail.trim().toLowerCase();
    if (!v || !v.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (recipients.includes(v)) {
      toast.error("Already in the list");
      return;
    }
    if (recipients.length >= 10) {
      toast.error("Max 10 CPA contacts");
      return;
    }
    setRecipients([...recipients, v]);
    setDraftEmail("");
  };

  const removeRecipient = (e) => setRecipients(recipients.filter((r) => r !== e));

  const send = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one CPA email");
      return;
    }
    setSending(true);
    try {
      const body = {
        recipients,
        save_as_default: saveDefault,
        note: note.trim() || undefined,
      };
      if (year !== "all") body.year = Number(year);
      const r = await api.post("/lease-vault/email-cpa", body);
      const leaseLabel = `${r.data.lease_count} lease${r.data.lease_count === 1 ? "" : "s"}`;
      const rcptLabel = `${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`;
      // Surface SMS status when the toggle fired so the user knows the heads-up went out
      const smsStatus = r.data?.sms?.status;
      if (smsStatus === "sent" || smsStatus === "mock") {
        toast.success(`Sent ${leaseLabel} to ${rcptLabel} · CPA also notified via SMS`);
      } else if (smsStatus === "error" || smsStatus === "skipped") {
        toast.success(`Sent ${leaseLabel} to ${rcptLabel} (SMS ${smsStatus})`);
      } else {
        toast.success(`Sent ${leaseLabel} to ${rcptLabel}`);
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="vault-email-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Send Lease Vault to CPA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-xs text-slate-500">
            Sends a year-scoped, formatted lease inventory (property, tenant, term, monthly rent, signing status) to your CPA contacts. Useful at year-end for K-1 prep and Schedule E reconciliation.
          </p>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">CPA email recipients</label>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="cpa@firm.com"
                className="input-clean flex-1"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                disabled={loadingContacts}
                data-testid="vault-email-input"
              />
              <button
                type="button"
                onClick={addRecipient}
                className="btn-outline-dark inline-flex items-center gap-1"
                data-testid="vault-email-add-recipient"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {recipients.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="vault-recipient-chips">
                {recipients.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-sm px-2 py-1 text-slate-700">
                    {e}
                    <button type="button" onClick={() => removeRecipient(e)} className="text-slate-400 hover:text-rose-600" aria-label={`Remove ${e}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            <label className="flex items-center gap-2 text-xs text-slate-600 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={saveDefault}
                onChange={(e) => setSaveDefault(e.target.checked)}
                data-testid="vault-save-default"
              />
              Save as default for next time
            </label>
          </div>

          {/* SMS-the-CPA toggle — fires a heads-up text whenever a binder email
              goes out. Phone + toggle are persisted to the user profile so
              the next email send picks them up automatically (also fires from
              the Jan 1 scheduler job). */}
          <div className="rounded-sm border border-violet-200 bg-violet-50/40 p-3" data-testid="vault-sms-section">
            <div className="flex items-center justify-between mb-1.5">
              <label className="flex items-center gap-2 text-xs font-semibold text-violet-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsOnBinder}
                  onChange={(e) => setSmsOnBinder(e.target.checked)}
                  className="accent-violet-700"
                  data-testid="vault-sms-toggle"
                />
                Also SMS my CPA when this email is sent
              </label>
              <button type="button"
                onClick={saveSmsPrefs}
                disabled={savingSms}
                data-testid="vault-sms-save-btn"
                className="text-[10px] font-mono uppercase tracking-widest text-violet-700 hover:text-violet-900 disabled:opacity-50">
                {savingSms ? "Saving…" : "Save SMS settings"}
              </button>
            </div>
            <input
              type="tel"
              placeholder="+1 555 123 4567"
              className="input-clean w-full text-sm font-mono disabled:opacity-50"
              value={cpaPhone}
              onChange={(e) => setCpaPhone(e.target.value)}
              disabled={loadingContacts}
              data-testid="vault-cpa-phone-input"
            />
            <div className="text-[10px] text-violet-700/70 mt-1.5 leading-snug">
              We'll send a single SMS to this number whenever the lease binder email goes out (manual send or the annual Jan 1 auto-send). E.164 format works best — e.g. <code className="font-mono">+15551234567</code>.
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Year filter</label>
            <select
              className="input-clean w-full"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="vault-email-year-select"
            >
              <option value="all">All years (full vault)</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Note to CPA (optional)</label>
            <textarea
              rows={3}
              maxLength={1000}
              className="input-clean w-full"
              placeholder="e.g. Here's the 2025 lease summary for Schedule E. Renewal on Suite 201 closes mid-March."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="vault-email-note"
            />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-outline-dark" data-testid="vault-email-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || recipients.length === 0}
            className="btn-primary-dark inline-flex items-center gap-2"
            data-testid="vault-email-send"
          >
            <Mail className="w-4 h-4" /> {sending ? "Sending…" : "Send to CPA"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
